import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/client';
import ModuleShell from '../../components/ModuleShell';
import RefreshRollupButton, {
  useRollupRefreshMessage,
} from '../../components/RefreshRollupButton';
import TimeSeriesChart, { formatChartDate } from '../../components/TimeSeriesChart';
import {
  ADMIN_KPI_SUMMARY,
  ADMIN_PAID_SIGNUPS_BY_SOURCE,
  ADMIN_SIGNUP_SERIES,
  ADMIN_SIGNUPS_BY_TIER_SERIES,
  ADMIN_USERS,
} from '../../graphql/adminQueries';
import { adminEnvLabel, currentAdminEnv } from '../../utils/envCopy';
import { formatAverage, formatNumber } from '../../utils/format';
import { PAID_CONVERSION_INTEGRITY_CAPTION } from '../../metrics/customerMetricsIntegrity';
import { RangeSelect, useDateRange } from '../home/DateRangeContext';

const SIGNUP_COHORT_ALLOWLIST = new Set(['free', 'basic', 'premium', 'enterprise', 'unknown']);
const SIGNUP_COHORT_COLUMN_ORDER = [
  'unknown',
  'free',
  'basic',
  'premium',
  'enterprise',
  'other',
];
const SIGNUP_COHORT_LABELS = {
  unknown: 'Unknown / legacy',
  free: 'Free',
  basic: 'Basic',
  premium: 'Premium',
  enterprise: 'Enterprise',
  other: 'Other',
};

export const UNKNOWN_SIGNUP_CAPTION =
  'Unknown / legacy = created before signup-tier stamp. Not inferred from current plan. Includes unverified and deactivated accounts.';

function toUtcDateOnly(value) {
  if (value == null) return null;
  if (typeof value === 'string') {
    const day = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  return null;
}

/** Map a series slug onto the locked allowlist before any object key. */
export function collapseSignupCohortSlug(rawSlug) {
  const slug = typeof rawSlug === 'string' && rawSlug.length > 0 ? rawSlug : 'unknown';
  if (SIGNUP_COHORT_ALLOWLIST.has(slug)) return slug;
  return 'other';
}

/** Monday-UTC week start, copied from API `getSignupSeries` WEEKLY. */
export function mondayUtcWeekStart(dateStr) {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  const day = d.getUTCDay();
  const diff = (day + 6) % 7;
  d.setUTCDate(d.getUTCDate() - diff);
  return d.toISOString().slice(0, 10);
}

/** UTC calendar month key `YYYY-MM-01`. */
export function monthUtcStart(dateStr) {
  return `${dateStr.slice(0, 7)}-01`;
}

/**
 * Rebucket DAILY sparse `{ date, slug, count }` into Daily/Weekly/Monthly rows.
 * Columns are `unknown` plus slugs with count > 0 after rebucket.
 */
export function rebucketSignupTierSeries(points, granularity = 'DAILY') {
  const counts = new Map();
  for (const point of points || []) {
    const day = toUtcDateOnly(point?.date);
    if (!day) continue;
    const slug = collapseSignupCohortSlug(point?.slug);
    let period = day;
    if (granularity === 'WEEKLY') period = mondayUtcWeekStart(day);
    if (granularity === 'MONTHLY') period = monthUtcStart(day);
    const n = Number(point?.count);
    const add = Number.isFinite(n) ? n : 0;
    if (!add) continue;
    const key = `${period}\t${slug}`;
    counts.set(key, (counts.get(key) || 0) + add);
  }

  const byPeriod = new Map();
  const slugsSeen = new Set();
  for (const [key, count] of counts) {
    const tab = key.indexOf('\t');
    const period = key.slice(0, tab);
    const slug = key.slice(tab + 1);
    slugsSeen.add(slug);
    if (!byPeriod.has(period)) byPeriod.set(period, new Map());
    byPeriod.get(period).set(slug, count);
  }

  const columns = SIGNUP_COHORT_COLUMN_ORDER.filter((slug) => slugsSeen.has(slug));
  const rows = [...byPeriod.keys()].sort().map((period) => {
    const bucket = byPeriod.get(period);
    const countsBySlug = {};
    let total = 0;
    for (const slug of columns) {
      const n = bucket.get(slug) || 0;
      countsBySlug[slug] = n;
      total += n;
    }
    return { period, counts: countsBySlug, total };
  });

  return { columns, rows, liveTotal: rows.reduce((sum, row) => sum + row.total, 0) };
}

export const SPEND_NOT_ENTERED = 'Spend not entered for this range';

export const PAID_SOURCE_FOOTNOTE =
  'Paid = distinct users with subscription_started in range, by first-touch source — not the CRO checkout_completed funnel end.';

export const ADS_ENABLE_FOOTER = 'Do not ENABLE campaigns from this console.';

/** localStorage key: source + range.from + range.to + env (AWG-ADS-3). */
export function growthSpendStorageKey({ source, from, to, env }) {
  return `ffc.admin.growthSpend.${env}.${from}.${to}.${source}`;
}

export function parseGrowthSpend(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * CPA cell. Hide the number until spend is entered for this source+range+env.
 * @returns {{ hidden: true, message: string } | { hidden: false, value: number|null }}
 */
export function cpaCell(spend, paid) {
  if (spend == null) {
    return { hidden: true, message: SPEND_NOT_ENTERED };
  }
  const paidCount = Number(paid);
  if (!Number.isFinite(paidCount) || paidCount <= 0) {
    return { hidden: false, value: null };
  }
  return { hidden: false, value: spend / paidCount };
}

export function formatCpaDollars(dollars) {
  if (dollars == null || !Number.isFinite(dollars)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(dollars);
}

/** Daily mix line for a signup-series hover, e.g. `1 Free · 1 Basic`. */
export function mixLineForDate(points, date) {
  const day = toUtcDateOnly(date);
  if (!day) return '';
  const counts = {};
  for (const point of points || []) {
    if (toUtcDateOnly(point?.date) !== day) continue;
    const slug = collapseSignupCohortSlug(point?.slug);
    const n = Number(point?.count);
    if (!Number.isFinite(n) || !n) continue;
    counts[slug] = (counts[slug] || 0) + n;
  }
  return SIGNUP_COHORT_COLUMN_ORDER.filter((slug) => counts[slug])
    .map((slug) => `${counts[slug]} ${SIGNUP_COHORT_LABELS[slug] || slug}`)
    .join(' · ');
}

function SignupDayTooltip({ date, count, mixLine }) {
  const [loadUsers, { data, loading }] = useLazyQuery(ADMIN_USERS, {
    fetchPolicy: 'network-only',
    errorPolicy: 'ignore',
  });

  useEffect(() => {
    if (!date || !count) return undefined;
    loadUsers({
      variables: { sort: 'RECENT_SIGNUP', createdOn: date, limit: 5, offset: 0 },
    });
    return undefined;
  }, [date, count, loadUsers]);

  const users = data?.adminUsers?.users || [];
  const day = toUtcDateOnly(date) || String(date).slice(0, 10);

  return (
    <>
      <p className="chart-hover-tip-date">{formatChartDate(date)}</p>
      <p>
        {formatNumber(count)} {count === 1 ? 'signup' : 'signups'}
        {mixLine ? ` · ${mixLine}` : ''}
      </p>
      {count > 0 ? (
        <>
          {loading && users.length === 0 ? <p>Loading users…</p> : null}
          {users.length > 0 ? (
            <ul className="chart-hover-users">
              {users.map((user) => (
                <li key={user.id}>
                  <Link to={`/users?userId=${encodeURIComponent(user.id)}`}>{user.email}</Link>
                </li>
              ))}
            </ul>
          ) : null}
          <Link className="chart-hover-tip-link" to={`/users?signedUpOn=${encodeURIComponent(day)}`}>
            View signups
          </Link>
        </>
      ) : null}
    </>
  );
}

function tierBars(table) {
  const totals = table.columns.map((slug) =>
    table.rows.reduce((sum, row) => sum + (row.counts[slug] || 0), 0)
  );
  const max = Math.max(1, ...totals);
  return table.columns
    .map((slug, index) => ({
      slug,
      label: SIGNUP_COHORT_LABELS[slug] || slug,
      value: totals[index],
      width: (totals[index] / max) * 100,
    }))
    .filter((row) => row.value > 0);
}

function TierMixBars({ table }) {
  const rows = tierBars(table);
  if (rows.length === 0) return null;
  return (
    <ul className="bar-chart" aria-label="Signups by tier">
      {rows.map((row) => (
        <li key={row.slug} className="bar-chart-row">
          <span className="bar-chart-label">
            <span className="bar-chart-label-primary">{row.label}</span>
          </span>
          <div className="bar-chart-track" aria-hidden="true">
            <div className="bar-chart-fill" style={{ width: `${row.width}%` }} />
          </div>
          <span className="bar-chart-value">{formatNumber(row.value)}</span>
        </li>
      ))}
    </ul>
  );
}

function GrowthGranularitySelect({ value, onChange }) {
  return (
    <>
      <label htmlFor="growth-granularity" className="range-label">
        Period
      </label>
      <select
        id="growth-granularity"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="DAILY">Daily</option>
        <option value="WEEKLY">Weekly</option>
        <option value="MONTHLY">Monthly</option>
      </select>
    </>
  );
}

function PaidSourceCpa({ spend, paid }) {
  const cell = cpaCell(spend, paid);
  if (cell.hidden) {
    return <span className="chart-hint">{cell.message}</span>;
  }
  return formatCpaDollars(cell.value);
}

function PaidBySourceTable({ rows, range }) {
  const env = currentAdminEnv();
  const [spendDraft, setSpendDraft] = useState({});

  useEffect(() => {
    const next = {};
    for (const row of rows || []) {
      const key = growthSpendStorageKey({
        source: row.source,
        from: range.from,
        to: range.to,
        env,
      });
      next[row.source] = localStorage.getItem(key) ?? '';
    }
    setSpendDraft(next);
  }, [rows, range.from, range.to, env]);

  function persistSpend(source, raw) {
    const key = growthSpendStorageKey({
      source,
      from: range.from,
      to: range.to,
      env,
    });
    if (raw === '') localStorage.removeItem(key);
    else localStorage.setItem(key, raw);
    setSpendDraft((prev) => ({ ...prev, [source]: raw }));
  }

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th scope="col">Source</th>
            <th scope="col">Signups</th>
            <th scope="col">Paid</th>
            <th scope="col">Spend</th>
            <th scope="col">CPA</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const spendRaw = spendDraft[row.source] ?? '';
            const spend = parseGrowthSpend(spendRaw);
            return (
              <tr key={row.source}>
                <td>{row.source}</td>
                <td>{formatNumber(row.signups)}</td>
                <td>{formatNumber(row.paid)}</td>
                <td>
                  <input
                    className="economics-inline-input economics-inline-input-wide"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    aria-label={`Spend for ${row.source}`}
                    value={spendRaw}
                    onChange={(event) => persistSpend(row.source, event.target.value)}
                  />
                </td>
                <td>
                  <PaidSourceCpa spend={spend} paid={row.paid} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function GrowthModule() {
  const { range } = useDateRange();
  const [granularity, setGranularity] = useState('DAILY');
  const [refreshMessage, setRefreshMessage] = useRollupRefreshMessage();

  const {
    data: seriesData,
    loading: seriesLoading,
    error: seriesError,
    refetch: refetchSeries,
  } = useQuery(ADMIN_SIGNUP_SERIES, {
    variables: { range },
  });

  const {
    data: kpiData,
    loading: kpiLoading,
    error: kpiError,
    refetch: refetchKpi,
  } = useQuery(ADMIN_KPI_SUMMARY, {
    variables: { range },
  });

  const {
    data: mixData,
    loading: mixLoading,
    error: mixError,
    refetch: refetchMix,
  } = useQuery(ADMIN_SIGNUPS_BY_TIER_SERIES, {
    variables: { range },
  });

  const {
    data: sourceData,
    loading: sourceLoading,
    error: sourceError,
    refetch: refetchSources,
  } = useQuery(ADMIN_PAID_SIGNUPS_BY_SOURCE, {
    variables: { range },
  });

  const signupSeries = seriesData?.adminSignupSeries || [];
  const kpi = kpiData?.adminKpiSummary;
  const loading = seriesLoading || kpiLoading;
  const queryError = seriesError || kpiError;

  const seriesTotal = signupSeries.reduce((sum, point) => sum + (point.count ?? 0), 0);
  const hasSignupData = signupSeries.length > 0 && seriesTotal > 0;
  const table = mixError
    ? null
    : rebucketSignupTierSeries(mixData?.adminSignupsByTierSeries, granularity);
  const showMixTable = !mixLoading && !mixError && table && table.rows.length > 0;
  const totalsDiffer =
    showMixTable && hasSignupData && table.liveTotal !== seriesTotal;

  return (
    <ModuleShell
      title="Growth"
      description="Signup time series, traffic sources, and membership mix for weekly GTM review."
      actions={
        <>
          <Link className="secondary-button" to="/growth/videos">
            Social content
          </Link>
          <RangeSelect id="growth-range" />
          <GrowthGranularitySelect value={granularity} onChange={setGranularity} />
          <RefreshRollupButton
            onMessage={setRefreshMessage}
            onRefreshed={() => Promise.all([refetchSeries(), refetchKpi(), refetchMix(), refetchSources()])}
          />
        </>
      }
      loading={loading}
      loadingLabel="Loading growth metrics…"
      error={queryError}
      errorLabel={`Could not load growth data. Confirm ${adminEnvLabel()} API admin queries are deployed.`}
    >
      {refreshMessage && <p className="module-status">{refreshMessage}</p>}
      {!loading && !queryError && (
        <>
          <div className="kpi-grid">
            <article className="kpi-card">
              <p className="kpi-label">Signups in range</p>
              <p className="kpi-value">{formatNumber(kpi?.signups ?? seriesTotal)}</p>
              <p className="kpi-hint">
                {range.from} → {range.to}
              </p>
            </article>
            <article className="kpi-card">
              <p className="kpi-label">Daily average</p>
              <p className="kpi-value">
                {formatAverage(
                  signupSeries.length ? seriesTotal / signupSeries.length : null
                )}
              </p>
              <p className="kpi-hint">
                {signupSeries.length
                  ? `${formatNumber(seriesTotal)} over ${signupSeries.length} days`
                  : 'From rollup series'}
              </p>
            </article>
            <article className="kpi-card">
              <p className="kpi-label">Total users</p>
              <p className="kpi-value">{formatNumber(kpi?.totalUsers)}</p>
              <p className="kpi-hint">End of range snapshot</p>
            </article>
          </div>

          <div className="growth-chart-grid">
            <section className="chart-card">
              <h2>Signup time series</h2>
              <p className="chart-hint">Daily new registrations from admin rollups</p>
              {hasSignupData ? (
                <TimeSeriesChart
                  series={signupSeries}
                  field="count"
                  mode="line"
                  className="time-series-chart"
                  ariaLabel="Daily signups from admin rollups"
                  renderTooltip={({ point, value }) => (
                    <SignupDayTooltip
                      date={point?.date}
                      count={value}
                      mixLine={mixLineForDate(mixData?.adminSignupsByTierSeries, point?.date)}
                    />
                  )}
                />
              ) : (
                <p className="chart-empty">No signup data in range. Rollup job may need to run.</p>
              )}
            </section>

            <section className="chart-card">
              <h2>Signups by tier at signup</h2>
              {mixError ? (
                <p className="chart-hint">Could not load period mix.</p>
              ) : mixLoading ? (
                <p className="chart-hint">Loading period mix…</p>
              ) : showMixTable ? (
                <TierMixBars table={table} />
              ) : (
                <p className="chart-empty">No cohort signups in range.</p>
              )}
            </section>
          </div>

          <section className="chart-card">
            <h2>Signup sources</h2>
            <p className="chart-hint">
              First-touch. Hibu (Google Ads) comes from cam=ppc-… or utm_source=hibu. Google Ads, TikTok, and Instagram come from utm or click id. Blank stays Unknown.
            </p>
            {sourceError ? (
              <p className="chart-hint">Could not load signup sources.</p>
            ) : sourceLoading ? (
              <p className="chart-hint">Loading signup sources…</p>
            ) : (sourceData?.adminPaidSignupsBySource || []).length > 0 ? (
              <PaidBySourceTable rows={sourceData.adminPaidSignupsBySource} range={range} />
            ) : (
              <p className="chart-empty">No signups in range.</p>
            )}
            <p className="chart-hint">{PAID_SOURCE_FOOTNOTE}</p>
            <p className="chart-hint">{PAID_CONVERSION_INTEGRITY_CAPTION}</p>
            <p className="module-footnote">{ADS_ENABLE_FOOTER}</p>
          </section>

          {showMixTable ? (
            <section className="chart-card">
              <h2>By period</h2>
              <p className="chart-hint">{UNKNOWN_SIGNUP_CAPTION}</p>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Period</th>
                      {table.columns.map((slug) => (
                        <th key={slug} scope="col">
                          {SIGNUP_COHORT_LABELS[slug] || slug}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row) => (
                      <tr key={row.period}>
                        <td>{row.period}</td>
                        {table.columns.map((slug) => (
                          <td key={slug}>{formatNumber(row.counts[slug])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          <p className="module-footnote">
            Daily totals come from rollup metrics. The period table is live users by
            tier at signup (UTC). Unknown is not inferred from current plan.
            {totalsDiffer
              ? ' Rollup total and live table total differ — check Operations freshness. Do not treat rollup tierCounts as signup mix.'
              : null}
          </p>
        </>
      )}
    </ModuleShell>
  );
}
